import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import UiCounter from '../Ui/UiCounter'
import UiSelectWithGraphics, {
    UiSelectOption,
} from '../Ui/UiSelectWithGraphics'

interface UiFormIntervalPickerProps {
    value: number | null
    onChange: (value: number | null) => void
    label?: string
    error?: string
    className?: string
}

type Unit = 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years'

const UNIT_FACTORS: Record<Unit, number> = {
    minutes: 1,
    hours: 60,
    days: 1440,
    weeks: 10080,
    months: 43200,
    years: 525600,
}

const UiFormIntervalPicker: React.FC<UiFormIntervalPickerProps> = ({
    value,
    onChange,
    label,
    error,
    className,
}) => {
    const { t } = useTranslation()

    const getInitialState = () => {
        const val = value ?? 1440
        const units: Unit[] = [
            'years',
            'months',
            'weeks',
            'days',
            'hours',
            'minutes',
        ]
        for (const u of units) {
            if (val % UNIT_FACTORS[u] === 0) {
                return { amount: val / UNIT_FACTORS[u], unit: u }
            }
        }
        return { amount: val, unit: 'minutes' as Unit }
    }

    const initialState = getInitialState()
    const [amount, setAmount] = useState<number | ''>(initialState.amount)
    const [unit, setUnit] = useState<Unit>(initialState.unit)

    useEffect(() => {
        if (amount === '') return

        const totalMinutes = (amount as number) * UNIT_FACTORS[unit]
        if (totalMinutes !== value) {
            onChange(totalMinutes)
        }
    }, [amount, unit])

    // Update internal state if external value changes significantly (not just from our own update)
    // We trust local state to drive the changes. External updates (like reset) might require a key change on the component to re-mount.

    const getUnitLabel = (u: Unit, count: number) => {
        const key = `interval_${u}`
        return t(key, { count })
    }

    const unitOptions: UiSelectOption[] = (
        Object.keys(UNIT_FACTORS) as Unit[]
    ).map((u) => ({
        value: u,
        label: getUnitLabel(u, typeof amount === 'number' ? amount : 0),
    }))

    return (
        <div
            className={clsx(
                'UiFormIntervalPicker flex flex-col w-full',
                className
            )}
        >
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {label}
                </label>
            )}
            <div className="flex items-center gap-3">
                <UiCounter
                    value={amount}
                    onChange={setAmount}
                    className="w-44"
                />

                <div className="flex-1 min-w-[140px]">
                    <UiSelectWithGraphics
                        options={unitOptions}
                        selected={unit}
                        setSelected={(val) => setUnit((val as Unit) || 'days')}
                        label=""
                    />
                </div>
            </div>
            {error && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
        </div>
    )
}

export default UiFormIntervalPicker
